import type { SignInProvider } from '@buakei/types';
import { Badge, Button, EmptyState, LoadingState, Notice } from '@buakei/ui';
import { AlertTriangle, ArrowRight, MailCheck } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { describeAuthError, type IdentityClient } from '@/auth/identity-client';
import { useAuth, type Session } from '@/auth/session';
import { DEMO_PERSONAS } from '@/demo/personas';
import { EXPERIENCE_META } from '@/shell/nav-config';

/** Only same-app paths are accepted as a post-sign-in destination (no open redirects). */
export function safeNext(value: string | null): string | null {
  return value && value.startsWith('/') && !value.startsWith('//') ? value : null;
}

/** Where a newly signed-in session should land. Accounts without workspaces see /account. */
export function destinationFor(session: Session, next: string | null): string {
  // Without a workspace, a verified account still has the shared library (ADR-016).
  if (session.experiences.length === 0) {
    if (!(session.provider === 'firebase' && session.emailVerified)) return '/account';
    return next?.startsWith('/library') ? next : '/library';
  }
  if (next) return next;
  return session.experiences.length > 1
    ? '/onboarding'
    : EXPERIENCE_META[session.experiences[0] ?? 'professional'].home;
}

const EMAIL_FOR_LINK_KEY = 'buakei.email-for-sign-in';

function rememberEmailForLink(email: string | null) {
  try {
    if (email) window.localStorage.setItem(EMAIL_FOR_LINK_KEY, email);
    else window.localStorage.removeItem(EMAIL_FOR_LINK_KEY);
  } catch {
    // Without storage the completion page simply asks for the email again.
  }
}

function recalledEmailForLink(): string | null {
  try {
    return window.localStorage.getItem(EMAIL_FOR_LINK_KEY);
  } catch {
    return null;
  }
}

export const inputClass =
  'h-10 w-full min-w-0 rounded-md border border-line-200 bg-white px-3 text-ink-900 placeholder:text-muted-600';

/** New accounts are created on /sign-up, where the password rules apply (ADR-010). */
type Mode = 'sign-in' | 'reset' | 'link';

const MODE_COPY: Record<Mode, { heading: string; submit: string }> = {
  'sign-in': { heading: 'Sign in with email', submit: 'Sign in' },
  reset: { heading: 'Reset your password', submit: 'Send reset email' },
  link: { heading: 'Get a sign-in link by email', submit: 'Email me a link' },
};

/** Real accounts (M2-01): Google, email and password, and email link (ADR-006). */
function AccountSignIn({ accounts }: { accounts: IdentityClient }) {
  const [mode, setMode] = useState<Mode>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const emailId = useId();
  const passwordId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const needsPassword = mode === 'sign-in';

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setMessage(null);
    setPassword('');
    headingRef.current?.focus();
  };

  const run = async (action: () => Promise<string | null>) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      setMessage(await action());
    } catch (e) {
      setError(describeAuthError(e));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const address = email.trim();
    void run(async () => {
      switch (mode) {
        case 'sign-in':
          await accounts.signInWithPassword(address, password);
          return null;
        case 'reset':
          await accounts.sendPasswordReset(address);
          // Same message whether or not the account exists (no account enumeration).
          return `If an account exists for ${address}, a password reset email is on its way.`;
        case 'link':
          await accounts.sendSignInLink(address);
          rememberEmailForLink(address);
          return `Check ${address} for a sign-in link, and open it in this browser.`;
      }
    });
  };

  return (
    <section
      aria-labelledby="account-sign-in"
      className="flex flex-col gap-4 rounded-lg border border-line-200 bg-white p-5"
    >
      <h2 id="account-sign-in" className="text-lg font-semibold text-navy-900">
        Your account
      </h2>
      <Button
        variant="secondary"
        disabled={busy}
        onClick={() =>
          void run(async () => {
            await accounts.signInWithGoogle();
            return null;
          })
        }
      >
        Continue with Google
      </Button>
      <div className="flex items-center gap-3 text-sm text-muted-600" aria-hidden>
        <span className="h-px flex-1 bg-line-200" /> or <span className="h-px flex-1 bg-line-200" />
      </div>
      <form className="flex flex-col gap-3" onSubmit={onSubmit}>
        <h3 ref={headingRef} tabIndex={-1} className="font-medium text-navy-900">
          {MODE_COPY[mode].heading}
        </h3>
        <div className="flex flex-col gap-1">
          <label htmlFor={emailId} className="text-sm font-medium text-ink-900">
            Email
          </label>
          <input
            id={emailId}
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
          />
        </div>
        {needsPassword ? (
          <div className="flex flex-col gap-1">
            <label htmlFor={passwordId} className="text-sm font-medium text-ink-900">
              Password
            </label>
            <input
              id={passwordId}
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
            />
          </div>
        ) : null}
        <Button type="submit" disabled={busy}>
          {busy ? 'Please wait…' : MODE_COPY[mode].submit}
        </Button>
      </form>
      {error ? (
        <p role="alert" className="flex gap-2 text-sm text-danger-700">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden /> {error}
        </p>
      ) : null}
      <p role="status" aria-live="polite" className="text-sm text-ink-900">
        {message}
      </p>
      <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
        {mode !== 'sign-in' ? (
          <Button variant="link" onClick={() => switchMode('sign-in')}>
            Sign in with a password
          </Button>
        ) : null}
        <Button asChild variant="link">
          <Link to="/sign-up">Create an account</Link>
        </Button>
        {mode !== 'reset' ? (
          <Button variant="link" onClick={() => switchMode('reset')}>
            Forgot password?
          </Button>
        ) : null}
        {mode !== 'link' ? (
          <Button variant="link" onClick={() => switchMode('link')}>
            Email me a sign-in link
          </Button>
        ) : null}
      </div>
    </section>
  );
}

function DemoPersonas({ next }: { next: string | null }) {
  const { signInAsPersona } = useAuth();
  const navigate = useNavigate();
  return (
    <ul className="grid gap-3 md:grid-cols-2">
      {DEMO_PERSONAS.map((persona) => (
        <li key={persona.id}>
          <button
            type="button"
            onClick={() => {
              const session = signInAsPersona(persona.id);
              if (!session) return;
              void navigate(destinationFor(session, next), { replace: true });
            }}
            className="group flex h-full w-full items-start justify-between gap-3 rounded-lg border border-line-200 bg-white p-5 text-left hover:border-navy-900/40"
          >
            <span className="flex flex-col gap-1">
              <span className="font-semibold text-navy-900">{persona.displayName}</span>
              <span className="text-sm text-muted-600">{persona.description}</span>
            </span>
            <ArrowRight
              className="mt-1 size-4 shrink-0 text-navy-900 transition-transform group-hover:translate-x-0.5"
              aria-hidden
            />
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Sign-in (AUTH-001): real accounts when configured, plus the clearly labelled DEMO personas. */
export function SignInPage() {
  const { session, status, accounts, notice } = useAuth();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));

  useEffect(() => {
    document.title = accounts ? 'Sign in · Buakei AI' : 'Sign in (demo) · Buakei AI';
  }, [accounts]);

  if (session) return <Navigate to={destinationFor(session, next)} replace />;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-12">
      {notice ? (
        <Notice title={notice} role="alert" icon={AlertTriangle} className="self-stretch" />
      ) : null}
      {accounts ? (
        <>
          <div className="flex flex-col gap-2">
            <h1 className="font-display text-4xl leading-tight text-navy-900">Sign in</h1>
            <p className="leading-relaxed text-muted-600">
              Signing in confirms who you are. Access to firm and learning workspaces is granted
              separately by an administrator.
            </p>
          </div>
          {status === 'checking' ? (
            <LoadingState label="Loading your account" />
          ) : (
            <AccountSignIn accounts={accounts} />
          )}
          <section aria-labelledby="demo-personas" className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="demo-personas" className="text-lg font-semibold text-navy-900">
                Explore the demo instead
              </h2>
              <Badge tone="solid">DEMO</Badge>
            </div>
            <p className="text-sm text-muted-600">
              Fictitious personas with demonstration data. No account or personal details needed.
            </p>
            <DemoPersonas next={next} />
          </section>
        </>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            <h1 className="font-display text-4xl leading-tight text-navy-900">
              Choose a demo persona
            </h1>
            <p className="leading-relaxed text-muted-600">
              This is a demonstration sign-in. Account sign-in is not set up in this environment; no
              passwords or personal details are collected here, and every persona is fictitious.
            </p>
          </div>
          {status === 'checking' ? (
            <LoadingState label="Checking sign-in options" />
          ) : (
            <DemoPersonas next={next} />
          )}
        </>
      )}
    </div>
  );
}

/** Completes an email-link sign-in. Firebase requires the same email to be confirmed. */
export function SignInCompletePage() {
  const { session, status, accounts } = useAuth();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsEmail, setNeedsEmail] = useState(false);
  const started = useRef(false);
  const emailId = useId();
  const href = window.location.href;
  const validLink = accounts?.isSignInLink(href) ?? false;

  useEffect(() => {
    document.title = 'Finishing sign-in · Buakei AI';
  }, []);

  const complete = async (client: IdentityClient, address: string) => {
    setBusy(true);
    setError(null);
    try {
      await client.completeSignInLink(address, href);
      rememberEmailForLink(null);
    } catch (e) {
      setError(describeAuthError(e));
      setNeedsEmail(true);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!accounts || !validLink || started.current) return;
    started.current = true;
    const stored = recalledEmailForLink();
    if (stored) void complete(accounts, stored);
    else setNeedsEmail(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, validLink]);

  if (session) return <Navigate to={destinationFor(session, null)} replace />;

  let body;
  if (!accounts) {
    body =
      status === 'checking' ? (
        <LoadingState label="Checking your sign-in link" />
      ) : (
        <EmptyState title="Account sign-in is not available here">
          <Link className="font-medium text-navy-900 underline" to="/sign-in">
            Return to sign-in
          </Link>
        </EmptyState>
      );
  } else if (!validLink) {
    body = (
      <EmptyState title="This is not a valid sign-in link">
        <p>
          It may be incomplete or already used.{' '}
          <Link className="font-medium text-navy-900 underline" to="/sign-in">
            Request a new link
          </Link>
        </p>
      </EmptyState>
    );
  } else if (needsEmail) {
    body = (
      <form
        className="flex flex-col gap-3 rounded-lg border border-line-200 bg-white p-5"
        onSubmit={(event) => {
          event.preventDefault();
          void complete(accounts, email.trim());
        }}
      >
        <p className="text-sm text-ink-900">
          For your security, confirm the email address the link was sent to.
        </p>
        <label htmlFor={emailId} className="text-sm font-medium text-ink-900">
          Email
        </label>
        <input
          id={emailId}
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputClass}
        />
        <Button type="submit" disabled={busy}>
          {busy ? 'Please wait…' : 'Finish signing in'}
        </Button>
        {error ? (
          <p role="alert" className="text-sm text-danger-700">
            {error}
          </p>
        ) : null}
      </form>
    );
  } else {
    body = <LoadingState label="Signing you in" />;
  }

  return (
    <div className="mx-auto flex max-w-xl flex-col gap-6 px-4 py-12">
      <h1 className="font-display text-4xl leading-tight text-navy-900">Finishing sign-in</h1>
      {body}
    </div>
  );
}

const PROVIDER_LABEL: Record<SignInProvider, string> = {
  password: 'Email (password or sign-in link)',
  'google.com': 'Google',
};

/** A signed-in account's identity, email verification and workspace access (AUTH-001). */
export function AccountPage() {
  const { session, status, accounts, signOut } = useAuth();
  const signedOutHere = useRef(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Your account · Buakei AI';
  }, []);

  if (!session) {
    if (signedOutHere.current) return <Navigate to="/" replace />;
    return status === 'checking' ? (
      <LoadingState label="Checking your sign-in" className="mx-auto max-w-xl p-6" />
    ) : (
      <Navigate to="/sign-in?next=%2Faccount" replace />
    );
  }

  const act = async (action: () => Promise<void>, done: string) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await action();
      setMessage(done);
    } catch (e) {
      setError(describeAuthError(e));
    } finally {
      setBusy(false);
    }
  };

  const isAccount = session.provider === 'firebase';

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-12">
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-4xl leading-tight text-navy-900">Your account</h1>
        {!isAccount ? (
          <p className="text-muted-600">
            You are using a fictitious DEMO persona. Nothing here is a real account.
          </p>
        ) : null}
      </div>

      <dl className="grid gap-x-6 gap-y-3 rounded-lg border border-line-200 bg-white p-5 sm:grid-cols-[max-content_1fr]">
        <dt className="text-sm font-medium text-muted-600">Name</dt>
        <dd className="text-ink-900">{session.displayName}</dd>
        {isAccount ? (
          <>
            <dt className="text-sm font-medium text-muted-600">Email</dt>
            <dd className="break-all text-ink-900">{session.email ?? 'Not provided'}</dd>
            <dt className="text-sm font-medium text-muted-600">Sign-in method</dt>
            <dd className="text-ink-900">
              {session.signInProvider ? PROVIDER_LABEL[session.signInProvider] : 'Unknown'}
            </dd>
            <dt className="text-sm font-medium text-muted-600">Email verified</dt>
            <dd className="text-ink-900">{session.emailVerified ? 'Yes' : 'Not yet'}</dd>
            <dt className="text-sm font-medium text-muted-600">Account ID</dt>
            <dd className="flex flex-wrap items-center gap-2">
              <code className="rounded bg-navy-50 px-1.5 py-0.5 font-mono text-sm break-all text-ink-900">
                {session.uid}
              </code>
              <Button
                size="sm"
                variant="link"
                onClick={() =>
                  void navigator.clipboard
                    ?.writeText(session.uid)
                    .then(() => setMessage('Account ID copied.'))
                    .catch(() => setMessage('Select the ID and copy it manually.'))
                }
              >
                Copy
              </Button>
            </dd>
          </>
        ) : null}
      </dl>

      {isAccount && session.emailVerified ? (
        <p className="text-sm text-ink-900">
          <Link to="/your-data" className="font-medium text-navy-900 underline">
            Your data
          </Link>
          : download what Buakei AI holds about you, delete your chat history, or ask for your
          account to be deleted.
        </p>
      ) : null}

      {isAccount && !session.emailVerified && accounts ? (
        <Notice title="Verify your email address" icon={MailCheck}>
          <p>
            Open the link we emailed to {session.email ?? 'you'}.{' '}
            {session.withheld === 'email_unverified'
              ? 'Your workspace access is on hold until you do.'
              : 'Workspace access requires a verified email.'}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={busy}
              onClick={() =>
                void act(() => accounts.reloadUser(), 'Checked your verification status.')
              }
            >
              I have verified my email
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() =>
                void act(() => accounts.resendVerification(), 'Verification email sent again.')
              }
            >
              Resend verification email
            </Button>
          </div>
        </Notice>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-danger-700">
          {error}
        </p>
      ) : null}
      <p role="status" aria-live="polite" className="text-sm text-ink-900">
        {message}
      </p>

      <section aria-labelledby="workspace-access" className="flex flex-col gap-3">
        <h2 id="workspace-access" className="text-lg font-semibold text-navy-900">
          Workspace access
        </h2>
        {session.experiences.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {session.experiences.map((experience) => (
              <li key={experience}>
                <Link
                  className="font-medium text-navy-900 underline"
                  to={EXPERIENCE_META[experience].home}
                >
                  {EXPERIENCE_META[experience].label}
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState title="No workspaces yet">
            <p>
              Firm and learning workspaces are granted by a Buakei AI administrator. Share your
              email address with them to request access. Meanwhile, you can explore every workspace
              with the DEMO personas after signing out.
            </p>
          </EmptyState>
        )}
        {session.provider === 'firebase' && session.emailVerified ? (
          <p>
            <Link className="font-medium text-navy-900 underline" to="/library">
              Open the legal library
            </Link>{' '}
            <span className="text-muted-600">— available to every verified account.</span>
          </p>
        ) : null}
      </section>

      <Button
        variant="secondary"
        className="self-start"
        onClick={() => {
          signedOutHere.current = true;
          signOut();
        }}
      >
        Sign out
      </Button>
    </div>
  );
}

/** Onboarding / workspace selection for accounts with more than one experience (PLAT-003). */
export function OnboardingPage() {
  const { session } = useAuth();
  useEffect(() => {
    document.title = 'Choose your workspace · Buakei AI';
  }, []);
  if (!session) return <Navigate to="/sign-in" replace />;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-12">
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-4xl leading-tight text-navy-900">
          Welcome, {session.displayName}
        </h1>
        <p className="text-muted-600">
          Your account includes more than one workspace. Each keeps its data separate. You can
          switch at any time from the top bar.
        </p>
      </div>
      <ul className="grid gap-3 md:grid-cols-2">
        {session.experiences.map((experience) => {
          const meta = EXPERIENCE_META[experience];
          return (
            <li key={experience}>
              <Link
                to={meta.home}
                className="flex h-full items-start gap-3 rounded-lg border border-line-200 bg-white p-5 hover:border-navy-900"
              >
                <meta.icon className="mt-0.5 size-5 text-navy-900" aria-hidden />
                <span className="flex flex-col gap-1">
                  <span className="font-semibold text-navy-900">{meta.label}</span>
                  <span className="text-sm text-muted-600">{meta.scope}</span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
